/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { Keys, containsKey, isKeyDefined, addKey, removeKey } from '../../utils/UNSAFE_keys';
import { SelectionDetail, SelectionMode } from '../../UNSAFE_Collection';
import { useUser } from '../UNSAFE_useUser';
import { getIsSelectionPending } from '#utils/PRIVATE_textSelectionUtils';
import { SelectionBehavior, SelectionRangeDetail } from './Selection.types';

const arrowKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);

/**
 * To get the event type during range selection which is triggered by either of the following gestures
 * shift+space, shift+click, and shift+arrowKeys
 * @param event
 */
const getEventType = (event: MouseEvent | KeyboardEvent) => {
  if (event.type === 'click') {
    return 'shiftClick';
  } else if (event.type === 'keyup' && (event as KeyboardEvent).key === ' ') {
    return 'shiftSpace';
  } else if (event.type === 'keydown' && arrowKeys.has((event as KeyboardEvent).key)) {
    return 'shiftArrowKeys';
  }
  return undefined;
};

const isShiftModeSupported = <K>(
  anchorKey: K,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  isClickEvent: boolean
) => {
  return selectionMode === 'multiple' && (isClickEvent || containsKey(selectedKeys, anchorKey));
};

const handleSelect = <K>(
  event: MouseEvent | KeyboardEvent,
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  selectionBehavior: SelectionBehavior,
  isSelectionRequired: boolean,
  keyboardCursorRef: RefObject<K | undefined>,
  anchorKey?: K,
  onChange?: (detail: SelectionDetail<K>) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void
) => {
  const itemKey = keyExtractor(event.target as HTMLElement);
  if (itemKey == null) {
    return;
  }

  // handle shift key selection
  if (
    event.shiftKey &&
    anchorKey !== undefined &&
    onRangeChange &&
    isShiftModeSupported(anchorKey, selectedKeys, selectionMode, event.type === 'click')
  ) {
    const selected = containsKey(selectedKeys, itemKey);
    const eventType = getEventType(event);
    if (selected && eventType === 'shiftSpace') {
      // during shift+space, deselect the item if it's already selected
      const keySet = removeKey(selectedKeys, itemKey, isSelectionRequired);
      onChange?.({ value: keySet, target: event.target });
    } else {
      onRangeChange({ value: { start: anchorKey, end: itemKey }, eventType });
    }
    // set keyboard selection cursor in case user extends with SHIFT+ARROW
    if (keyboardCursorRef) {
      keyboardCursorRef.current = itemKey;
    }
    return;
  }

  const selected = containsKey(selectedKeys, itemKey);
  let keySet = selectedKeys;
  if (!selected) {
    if (
      selectionMode === 'single' ||
      (event as KeyboardEvent).key === 'Enter' ||
      (selectionBehavior === 'replace' &&
        !(event.ctrlKey || event.metaKey || (event as KeyboardEvent).key === ' '))
    ) {
      // should only contains this item key
      keySet = { all: false, keys: new Set([itemKey]) };
    } else {
      // add to existing selected keys
      keySet = addKey(selectedKeys, itemKey);
    }
  } else if ((event as KeyboardEvent).key !== 'Enter') {
    // no de-select for enter key
    if (
      selectionBehavior === 'toggle' ||
      event.ctrlKey ||
      event.metaKey ||
      (event as KeyboardEvent).key === ' '
    ) {
      // de-select only when ctrl/meta key is pressed or selectionBehavior is
      // explicitly set to toggle
      keySet = removeKey(selectedKeys, itemKey, isSelectionRequired);
    } else if (selectionMode === 'multiple' && (selectedKeys.all || selectedKeys.keys.size > 1)) {
      // in this case everything should be de-selected except for this key
      keySet = { all: false, keys: new Set([itemKey]) };
    }
  }

  // only invoke callback if there is really a change
  // note onSelectionChange should never be null here
  if (selectedKeys != keySet && onChange) {
    onChange({ value: keySet, target: event.target });
    if (event.type === 'click') {
      event.stopPropagation();
    }
  }
};

/**
 * Hook for handling various selection mode.  This can be reuse by all Collection components.
 * @param keyExtractor function to extract the key based on provided element
 * @param selectedKeys the currently selected keys
 * @param selectionMode the selection mode
 * @param isSelectionRequired true if empty selected key is not allowed, false otherwise
 * @param selectionBehavior the selection behavior.  If the value is 'toggle', then the key is
 *                          added/removed from the current selected keys.  If the value is 'replace',
 *                          then the current selected keys are cleared first before the key is added
 *                          unless the ctrl/shift/meta key is pressed.
 * @param onChange callback function that is invoked when selection has changed.
 * @param anchorKey the anchor key.  Usually this is the same as current key except when shift key is pressed.
 * @param currentKey the current key
 * @param getVerticalPrevNextKey function to get the previous/next key in the vertical direction based on the current key
 * @param getHorizontalPrevNextKey function to get the previous/next key in the horizontal direction based on the current key
 * @param scrollToKey function to scroll the item with the key to be visible
 * @param onRangeChange callback function that is invoked when the selection range has changed.
 * @returns an event map that the consumer can register as event listeners
 */
export function useSelection<K>(
  keyExtractor: (element: HTMLElement) => K | null,
  selectedKeys: Keys<K>,
  selectionMode: SelectionMode,
  isSelectionRequired: boolean,
  selectionBehavior: SelectionBehavior,
  onChange?: (detail: SelectionDetail<K>) => void,
  anchorKey?: K,
  currentKey?: K | undefined,
  getVerticalPrevNextKey?: (currentKey: K | undefined, isPrev: boolean) => () => K | null,
  getHorizontalPrevNextKey?: (currentKey: K | undefined, isPrev: boolean) => () => K | null,
  scrollToKey?: (key: K) => void,
  onRangeChange?: (detail: SelectionRangeDetail<K>) => void
) {
  // ref to track the cursor for the SHIFT+ARROW keyboard selection separately than the anchor of the selection.
  const keyboardSelectionCursor = useRef<K>();
  const pendingKeyboardSelectionCursor = useRef(false);

  const isTouchPointerRef = useRef<boolean>(false);

  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      isTouchPointerRef.current = event.pointerType === 'touch';
      if (event.shiftKey && selectionMode === 'multiple') {
        //Avoid text selection on pointer down
        event.preventDefault();
      }
    },
    [selectionMode, isTouchPointerRef]
  );

  const onClickCapture = useCallback((event: MouseEvent) => {
    if (!getIsSelectionPending()) {
      if (!event.shiftKey) {
        // reset keyboard cursor during capture phase, because Selector doesn't propagate click event
        keyboardSelectionCursor.current = undefined;
        pendingKeyboardSelectionCursor.current = false;
      } else {
        pendingKeyboardSelectionCursor.current = true;
      }
    }
  }, []);

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (!getIsSelectionPending()) {
        handleSelect<K>(
          event,
          keyExtractor,
          selectedKeys,
          selectionMode,
          isTouchPointerRef.current ? 'toggle' : selectionBehavior,
          isSelectionRequired,
          keyboardSelectionCursor,
          currentKey,
          onChange,
          onRangeChange
        );
        pendingKeyboardSelectionCursor.current = false;
      }
    },
    [
      keyExtractor,
      selectedKeys,
      selectionMode,
      selectionBehavior,
      isSelectionRequired,
      keyboardSelectionCursor,
      currentKey,
      onChange,
      onRangeChange
    ]
  );

  // general keyboard events listen to keyUp for a11y
  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // older browser that we don't support returns 'Spacebar' for space key
      if (event.key === ' ') {
        handleSelect<K>(
          event,
          keyExtractor,
          selectedKeys,
          selectionMode,
          selectionBehavior,
          isSelectionRequired,
          keyboardSelectionCursor,
          anchorKey,
          onChange,
          event.shiftKey && event.key === ' ' ? onRangeChange : undefined
        );
      }
    },
    [
      keyExtractor,
      selectedKeys,
      selectionMode,
      selectionBehavior,
      isSelectionRequired,
      anchorKey,
      onChange,
      onRangeChange
    ]
  );

  const { direction } = useUser();

  // arrowKey events listen to keyDown so they can support holding them down
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // prevent default (propagation) for keyDown to space bar causing container to scroll
      if (event.key === ' ') {
        event.preventDefault();
        return;
      }

      if (pendingKeyboardSelectionCursor.current) {
        keyboardSelectionCursor.current = undefined;
        pendingKeyboardSelectionCursor.current = false;
      }

      const key = keyboardSelectionCursor.current ?? currentKey;
      const isRtl = direction === 'rtl';

      const keyHandlers: Record<string, (() => K | null) | undefined> = {
        ArrowUp: getVerticalPrevNextKey?.(key, true),
        ArrowDown: getVerticalPrevNextKey?.(key, false),
        ArrowLeft: getHorizontalPrevNextKey?.(key, !isRtl),
        ArrowRight: getHorizontalPrevNextKey?.(key, isRtl)
      };
      const isBothDirections = getVerticalPrevNextKey && getHorizontalPrevNextKey;
      if (Object.keys(keyHandlers).includes(event.key)) {
        // check ctrl/meta keys to avoid conflict gesture with reorder, and reset the range
        if (
          event.shiftKey &&
          !(event.ctrlKey || event.metaKey) &&
          scrollToKey &&
          selectionMode === 'multiple'
        ) {
          //Avoid increasing range of selection text selection with arrow keys
          event.preventDefault();
          let keySet = selectedKeys;
          // on first selection event, set keyboard cursor to start at currentKey
          if (!isKeyDefined(keyboardSelectionCursor.current) && isKeyDefined(currentKey)) {
            keySet = { all: false, keys: new Set([currentKey as K]) };
          }
          const newAnchorKey = keyHandlers[event.key];
          const cursorKey = newAnchorKey?.();
          if (cursorKey != null) {
            // keep visible
            scrollToKey(cursorKey);

            // if in 2D, use range selection to extend or subtract
            if (isBothDirections) {
              if (onRangeChange && currentKey != null) {
                onRangeChange({
                  value: { start: currentKey, end: cursorKey },
                  eventType: getEventType(event)
                });
              }
            } else {
              // extend selection
              if (!containsKey(keySet, cursorKey)) {
                keySet = addKey(keySet, cursorKey);
                // subtract from selection
              } else if (
                keyboardSelectionCursor.current != null &&
                keyboardSelectionCursor.current !== cursorKey
              ) {
                keySet = removeKey(keySet, keyboardSelectionCursor.current, isSelectionRequired);
              }
              if (selectedKeys != keySet && onChange) {
                onChange({ value: keySet, target: event.target });
              }
            }
            // update selection cursor
            keyboardSelectionCursor.current = cursorKey;
          }
        } else {
          // reset keyBoard on non-shifted arrow use as this interrupts the extend mode (and would make for messy logic that doesn't align with current Jet behavior)
          keyboardSelectionCursor.current = undefined;
        }
      }
    },
    [
      selectedKeys,
      isSelectionRequired,
      onChange,
      currentKey,
      getVerticalPrevNextKey,
      getHorizontalPrevNextKey,
      scrollToKey,
      direction,
      onRangeChange,
      selectionMode
    ]
  );

  const selectionProps =
    selectionMode === 'none' || onChange == null
      ? {}
      : { onPointerDown, onClick, onClickCapture, onKeyDown, onKeyUp };
  return { selectionProps };
}
